import {SubAccountEntry} from '@/incoming-balance/types/SubAccountEntry.type'
import {computed, ref, Ref} from 'vue'
import {ColDef, ICellRendererParams} from 'ag-grid-community'
import {formatCurrency} from '@/services/formatter.service'
import {CsvGridComponentApi} from '@/incoming-balance/types/CsvGridComponentApi.type'
import {
    createSubAccountFromOnlyName,
    createSubAccountFromOrgNo,
    fetchCustomers,
    fetchSuppliers,
    findCustomerByName,
    findCustomerByOrgNo,
    findSubAccountByName,
    findSubAccountByOrgNo,
    findSubAccountByUuid,
    findSupplierByName,
    findSupplierByOrgNo,
    updateSubAccount
} from '@/subaccount/subAccount.service'
import {buildIncomingBalanceData, calculateTotal, getPostStatus, normalizeOrgNo} from '@/incoming-balance/incomingBalance.composable'
import _ from 'lodash'
import {parseNumber} from '@/services/numberParser.service'
import {CsvDataRow} from '@/incoming-balance/types/CsvDataRow.type'
import {ColumnMapping} from '@/incoming-balance/types/ColumnMapping.type'
import {format, isValid, parse} from 'date-fns'
import {CsvData} from '@/incoming-balance/types/CsvData.type'
import {getValue} from '@/incoming-balance/CsvColumnMapper.service'
import {CUSTOMER, SUPPLIER} from '@/subaccount/types/SubAccount'
import {v4 as uuidv4} from 'uuid'

const subAccColumnConfig: ColDef[] = [
    {
        headerName: 'Navn',
        field: 'subAccName'
    },
    {
        headerName: 'Org.nr',
        field: 'subAccIdentification'
    },
    {
        headerName: 'Fakturanr',
        field: 'invoiceNo'
    },
    {
        headerName: 'Forfallsdato',
        field: 'dueDate'
    },
    {
        headerName: 'Beløp',
        field: 'amount',
        cellClass: 'text-right',
        cellRenderer: ({value}: ICellRendererParams) => {
            if(!value) return
            value = value.replace(',', '.').replace(/\s+/g, '')
            const parsedFloat = parseFloat(value)

            if (isNaN(parsedFloat)) {
                return value
            } else {
                return formatCurrency(parsedFloat)
            }
        }
    }
]

const defaultColMap: ColumnMapping = {
    subAccName: 1,
    subAccIdentification: 2,
    invoiceNo: 8,
    dueDate: 5,
    amount: 11
}

const customerData = ref<CsvGridComponentApi>(
    buildIncomingBalanceData(defaultColMap, subAccColumnConfig)
)

const supplierData = ref<CsvGridComponentApi>(
    buildIncomingBalanceData(defaultColMap, subAccColumnConfig)
)

export const mapCsvDataRowToSubAccountEntry = (row: CsvDataRow, columnMapping: ColumnMapping): SubAccountEntry => {
    const accountName = row[columnMapping['subAccName']] as string
    const orgNo = row[columnMapping['subAccIdentification']] as string
    const invoiceNo = row[columnMapping['invoiceNo']] as string
    const amount = parseNumber(row[columnMapping['amount']])
    const transStatus = getPostStatus(amount, '', true)
    let dueDate = row[columnMapping['dueDate']] as string

    if (dueDate) {
        const parsedDate = parse(dueDate, 'dd.MM.yyyy', new Date())
        dueDate = format(parsedDate, 'yyyy-MM-dd')

        if (!isValid(parsedDate)) {
            dueDate = ''
        } else {
            dueDate = format(parsedDate, 'yyyy-MM-dd')
        }
    }

    const subAcc = orgNo ? findSubAccountByOrgNo(normalizeOrgNo(orgNo)) : findSubAccountByName(accountName, true)

    return {
        entryUuid: uuidv4(),
        accountName,
        orgNo,
        dueDate,
        invoiceNo: invoiceNo === '' ? undefined : invoiceNo,
        balance: amount,
        transStatus,
        subAccountUuid: subAcc?.uuid
    }
}

const mapSubAccCsvToEntries = (data: Ref<CsvGridComponentApi>): SubAccountEntry[] => {
    const {csvData, columnMapping} = data.value
    if (!csvData || csvData.length === 0) return []

    return csvData
        .filter((_: any, index: number) => !(data.value.firstRowIsHeader && index === 0))
        .map((row: CsvDataRow) => mapCsvDataRowToSubAccountEntry(row, columnMapping))
}


const useSubAccBalance = (subAccData: Ref<CsvGridComponentApi>, isCustomer: Boolean) => {
    const totalBalance = computed(() => calculateTotal(subAccData, 'amount'))
    const entries = computed(() => mapSubAccCsvToEntries(subAccData))

    const findSubAccByName = isCustomer ? findCustomerByName : findSupplierByName
    const findSubAccByOrgNo = isCustomer ? findCustomerByOrgNo : findSupplierByOrgNo

    const normalizeName = (name?: string) => name?.trim().toLowerCase() ?? ''

    // MISSING
    const isSubAccountMissing = (row: CsvDataRow): boolean => {
        const subAcc = findSubAccountForRow(row)
        return !subAcc
    }
    const missingSubAccounts = computed<number>(() => {
        const missing = _(subAccData.value.csvData)
            .reject((row: CsvDataRow | null) => row === null)
            .reject((_, index: number) => {
                return subAccData.value.firstRowIsHeader && index === 0
            })
            .filter(isSubAccountMissing)
            .uniqBy((row: CsvDataRow) => {
                const name = getValue('subAccName', row, subAccData.value.columnMapping) as string
                const orgNo = getValue('subAccIdentification', row, subAccData.value.columnMapping) as string
                return normalizeOrgNo(orgNo || '') || normalizeName(name)
            })
            .value()
        return missing.length
    })
    const createMissingSubAccount = async (row: CsvDataRow) => {
        const subAccountUuid = row[row.length - 1]
        if (!subAccountUuid) {
            const name = getValue('subAccName', row, subAccData.value.columnMapping) as string
            const orgNo = getValue('subAccIdentification', row, subAccData.value.columnMapping) as string

            const subAcc = findSubAccountForRow(row)
            if (!subAcc) {
                if (orgNo) {
                    await createSubAccountFromOrgNo(normalizeOrgNo(orgNo), isCustomer ? CUSTOMER : SUPPLIER)
                } else if (name) {
                    await createSubAccountFromOnlyName(name, isCustomer ? CUSTOMER : SUPPLIER)
                }
                isCustomer ? await fetchCustomers() : await fetchSuppliers()
                assignSubAccountUuids()
            }
        }
    }

    const createMissingSubAccounts = async () => {
        for (const [index, row] of subAccData.value.csvData.entries()) {
            if (subAccData.value.firstRowIsHeader && index === 0) continue
            await createMissingSubAccount(row)
        }
    }

    // MISNAMED
    const isSubAccountMisnamed = (row: CsvDataRow): boolean => {
        const subAccUuid = row[row.length - 1] as string
        if (!subAccUuid) return false
        const subAcc = findSubAccountByUuid(subAccUuid)
        if (!subAcc) return false
        const name = getValue('subAccName', row, subAccData.value.columnMapping) as string
        return !!name && name !== subAcc.name
    }
    const misnamedSubAccounts = computed<number>(() => {
        const misnamed = _(subAccData.value.csvData)
            .reject((row: CsvDataRow | null) => row === null)
            .reject((_, index: number) => {
                return subAccData.value.firstRowIsHeader && index === 0
            })
            .filter(isSubAccountMisnamed)
            .uniqBy((row: CsvDataRow) => {
                const orgNo = getValue('subAccIdentification', row, subAccData.value.columnMapping) as string
                return normalizeOrgNo(orgNo || '')
            })
            .value()

        return misnamed.length
    })
    const correctMisnamedSubAccount = async (row: CsvDataRow) => {
        if (isSubAccountMisnamed(row)) {
            const name = getValue('subAccName', row, subAccData.value.columnMapping) as string
            const uuid = row[row.length - 1] as string
            const subAcc = findSubAccountByUuid(uuid)
            if (!subAcc) return
            return await updateSubAccount({
                ...subAcc,
                name
            })
        }
    }
    const correctMisnamedSubAccounts = async () => {
        const promises = []
        for (const [index, row] of subAccData.value.csvData.entries()) {
            if (subAccData.value.firstRowIsHeader && index === 0) continue
            promises.push(correctMisnamedSubAccount(row))
        }
        await Promise.all(promises)
        isCustomer ? await fetchCustomers() : await fetchSuppliers()
    }

    // INACTIVE
    const subAccountNeedsActivation = (row: CsvDataRow): boolean => {
        const subAccUuid = row[row.length - 1] as string
        if (!subAccUuid) return false
        const subAcc = findSubAccountByUuid(subAccUuid)
        if (!subAcc) return false
        return !subAcc.active
    }
    const inactiveSubAccounts = computed<number>(() => {
        const inactive = _(subAccData.value.csvData)
            .reject((row: CsvDataRow | null) => row === null)
            .reject((_, index: number) => {
                return subAccData.value.firstRowIsHeader && index === 0
            })
            .filter(subAccountNeedsActivation)
            .uniqBy((row: CsvDataRow) => {
                return row[row.length - 1]
            })
            .value()
        return inactive.length
    })
    const activateSubAccount = async (row: CsvDataRow) => {
        const subAccUuid = row[row.length - 1] as string
        const subAcc = findSubAccountByUuid(subAccUuid)
        if (subAcc) {
            return updateSubAccount({
                ...subAcc,
                active: true
            })
        }
    }
    const activateAllInactiveSubAccounts = async () => {
        const promises = []
        for (const [index, row] of subAccData.value.csvData.entries()) {
            if (subAccData.value.firstRowIsHeader && index === 0) continue
            promises.push(activateSubAccount(row))
        }
        await Promise.all(promises)
        isCustomer ? await fetchCustomers() : await fetchSuppliers()
    }

    // DUPLICATED INVOICE NOS
    const subAccountHasDuplicateInvoice = (row: CsvDataRow): boolean => {
        const invoiceNo = getValue('invoiceNo', row, subAccData.value.columnMapping) as string
        if (!invoiceNo) return false

        // Count duplicates by iterating over raw CSV rows instead of mapped entries
        let duplicateCount = 0
        subAccData.value.csvData.forEach((csvRow: CsvDataRow, index: number) => {
            if (subAccData.value.firstRowIsHeader && index === 0) return
            const rowInvoiceNo = getValue('invoiceNo', csvRow, subAccData.value.columnMapping) as string
            if (rowInvoiceNo && rowInvoiceNo === invoiceNo) {
                duplicateCount += 1
            }
        })
        return duplicateCount > 1
    }
    
    const duplicatedInvoiceNos = computed<number>(() => {
        return subAccData.value.csvData.filter(subAccountHasDuplicateInvoice).length
    })

    // MISSING DUE DATE
    const subAccountIsMissingDueDate = (row: CsvDataRow): boolean => {
        // If invoiceNo exists, dueDate must also exist
        const invoiceNo = getValue('invoiceNo', row, subAccData.value.columnMapping) as string
        const dueDate = getValue('dueDate', row, subAccData.value.columnMapping) as string
        return !!invoiceNo && !dueDate
    }

    const missingDueDates = computed<number>(() => {
        return subAccData.value.csvData.filter(subAccountIsMissingDueDate).length
    })

    const setCsvData = (data: CsvData) => {
        subAccData.value.csvData = (data as string[][])
            .filter(row => row.some(cell => cell.trim() !== ''))
            .map(row => ([...row, ''])) as CsvData
        assignSubAccountUuids()
    }

    const findSubAccountForRow = (row: CsvDataRow) => {
        const name = getValue('subAccName', row, subAccData.value.columnMapping) as string
        const orgNo = getValue('subAccIdentification', row, subAccData.value.columnMapping) as string

         if(orgNo) return findSubAccByOrgNo(normalizeOrgNo(orgNo))
         if(name) return findSubAccByName(name, true)

         return undefined
    }

    const assignSubAccountUuidForRow = (row: CsvDataRow) => {
        const subAccUuid = row[row.length - 1] as string
        if (subAccUuid && findSubAccountByUuid(subAccUuid)) return
        const subAcc = findSubAccountForRow(row)

        if (subAcc) {
            row[row.length - 1] = subAcc.uuid
        }
    }

    const assignSubAccountUuids = () => {
        subAccData.value.csvData.forEach(assignSubAccountUuidForRow)
    }

    const reAssignSubAccountUuids = async () => {
        subAccData.value.csvData.forEach((row: CsvDataRow) => {
            row[row.length - 1] = ''
        })
        assignSubAccountUuids()
    }

    const reAssignSubAccountUuidForRow = (row: CsvDataRow) => {
        row[row.length - 1] = ''
        assignSubAccountUuidForRow(row)
    }

    const clear = () => {
        subAccData.value.csvData = []
        subAccData.value.fileName = ''
        subAccData.value.columnMapping = defaultColMap
    }

    return {
        subAccData,
        data: subAccData,
        entries,
        totalBalance,
        deviations: {
            missingSubAccounts,
            misnamedSubAccounts,
            inactiveSubAccounts,
            duplicatedInvoiceNos,
            missingDueDates
        },

        //missing
        isSubAccountMissing,
        createMissingSubAccount,
        createMissingSubAccounts,

        //misnamed
        isSubAccountMisnamed,
        correctMisnamedSubAccount,
        correctMisnamedSubAccounts,

        //inactive
        subAccountNeedsActivation,
        activateSubAccount,
        activateAllInactiveSubAccounts,

        //dupl invoice
        subAccountHasDuplicateInvoice,
        duplicatedInvoiceNos,

        //missing due date
        subAccountIsMissingDueDate,
        clear,
        setCsvData,

        reAssignSubAccountUuids,
        reAssignSubAccountUuidForRow,
    }
}

export default () => ({
    useCustomerBalance: () => useSubAccBalance(customerData, true),
    useSupplierBalance: () => useSubAccBalance(supplierData, false)
})